<template>
   <!-- Table action row: Add, Filter, Columns, More, and the panel icons -->
   <div class="d-flex flex-wrap justify-content-start align-items-center gap-1 p-1 bg-table-action-row">
      <!-- Rows ticked: this bar replaces the buttons until the selection is cleared -->
      <div v-if="selectedCount" class="d-flex align-items-center gap-2 w-100 table-selection-bar">
         <button type="button" class="btn btn-sm bg-transparent border-0 sa-table-action-color" title="Clear selection" @click="$emit('clear-selection')">
            <i class="fa fa-times"></i>
         </button>
         <strong>{{ selectedCount }} selected</strong>
         <div v-if="canDelete" class="ms-auto d-flex gap-1">
            <button v-if="deletedView" type="button" class="btn btn-sm btn-success" data-bs-toggle="modal" data-bs-target="#bulkActionModal">
               <i class="fa fa-undo"></i><span class="d-none d-sm-inline"> Restore</span>
            </button>
            <button v-else type="button" class="btn btn-sm btn-danger" data-bs-toggle="modal" data-bs-target="#bulkActionModal">
               <i class="fa fa-trash"></i><span class="d-none d-sm-inline"> Delete</span>
            </button>
         </div>
      </div>

      <div v-else class="d-flex ms-auto gap-1">
         <!-- Add: opens the record form -->
         <button
            v-if="canAdd"
            id="addRecordBtn"
            type="button"
            title="Add new (Alt+N)"
            class="btn btn-success btn-sm"
            data-bs-toggle="modal"
            data-bs-target="#recordFormModal"
            @click="$emit('add')"
         >
            <i class="fa fa-plus"></i><span class="d-none d-sm-inline"> Add</span>
         </button>

         <!-- Filter: blue with a count when filters are on -->
         <button
            v-if="showFilter"
            type="button"
            title="Filter data"
            class="btn btn-sm position-relative"
            :class="filterCount ? 'btn-primary' : 'btn-secondary'"
            data-bs-toggle="modal"
            data-bs-target="#filterDataModal"
            @click="$emit('filter')"
         >
            <i class="fa fa-filter"></i><span class="d-none d-sm-inline"> Filter</span>
            <span v-if="filterCount" class="badge bg-warning text-dark ms-1">{{ filterCount }}</span>
         </button>

         <!-- Columns: choose which columns to show -->
         <ColumnChooser
            v-if="columns.length"
            :model-value="hiddenColumns"
            :columns="columns"
            @update:model-value="$emit('update:hiddenColumns', $event)"
         />

         <!-- More: the page's own items (import, export, links ...) -->
         <div v-if="$slots.more" class="btn-group btn-group-sm">
            <button type="button" class="btn btn-secondary dropdown-toggle" data-bs-toggle="dropdown" title="More actions">
               <i class="fa fa-ellipsis-v"></i><span class="d-none d-sm-inline"> More</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
               <slot name="more" />
            </ul>
         </div>

         <!-- Any extra button a page needs -->
         <slot />

         <!-- Panel icons -->
         <div class="d-none d-md-block">
            <button class="btn btn-collapse bg-transparent border-0">
               <i class="fa fa-minus sa-table-action-color"></i>
            </button>
            <button class="btn btn-fullscreen bg-transparent border-0">
               <i class="fa fa-expand sa-table-action-color"></i>
            </button>
            <button v-if="showClose" class="btn btn-delete bg-transparent border-0">
               <i class="fa fa-times sa-table-action-color"></i>
            </button>
         </div>
      </div>

      <!-- Confirm window for the selection bar (same look as the row delete window) -->
      <DeleteModal
         modal-id="bulkActionModal"
         :title="deletedView ? 'Restore selected records' : 'Delete selected records'"
         :message="deletedView
            ? `Restore ${selectedCount} record(s)?`
            : `Delete ${selectedCount} record(s)? They move to Deleted Records and can be restored.`"
         @confirm="$emit('bulk-action', deletedView ? 'restore' : 'soft')"
      />
   </div>
</template>

<script>
// Shared table toolbar.
//   <TableToolbar :can-add="$hasPermission('x.add')" :filter-count="activeFilterCount"
//                 :columns="tableColumns" v-model:hidden-columns="hiddenColumns"
//                 @add="clearForm()" @filter="openFilters">
//      <template #more> <li>...</li> </template>
//   </TableToolbar>
import ColumnChooser from "./ColumnChooser.vue";
import DeleteModal from "./DeleteModal.vue";

export default {
   name: "TableToolbar",
   components: { ColumnChooser, DeleteModal },
   props: {
      canAdd: { type: Boolean, default: false },          // show the Add button
      showFilter: { type: Boolean, default: true },       // show the Filter button
      filterCount: { type: Number, default: 0 },          // activeFilterCount
      columns: { type: Array, default: () => [] },        // tableColumns (empty = no Columns button)
      hiddenColumns: { type: Array, default: () => [] },  // v-model:hidden-columns
      showClose: { type: Boolean, default: true },        // the × panel icon
      selectedCount: { type: Number, default: 0 },        // ticked rows: shows the selection bar
      deletedView: { type: Boolean, default: false },     // Deleted view: Restore instead of Delete
      canDelete: { type: Boolean, default: true },        // show Delete / Restore in the selection bar
   },
   emits: ["add", "filter", "update:hiddenColumns", "clear-selection", "bulk-action"],
};
</script>